import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "../utils/axiosInstance";
import {
  Table,
  Modal,
  Button,
  Form,
  Row,
  Col,
  Card,
  Badge,
  Spinner,
  Alert,
  InputGroup,
  Dropdown,
  DropdownButton,
  Pagination,
  OverlayTrigger,
  Tooltip,
  Offcanvas,
  Collapse,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

import { getApiBase } from "../utils/useApiBase";
const API_URL = `${getApiBase()}/ubuzima`;

const Ubuzima = ({ isUmudugudu = false }) => {
  // ════════════════════════════════════════════════════════════════════════
  // STATES
  // ════════════════════════════════════════════════════════════════════════
  const [ubuzima, setUbuzima] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [show, setShow] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: "", variant: "" });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [expandedCard, setExpandedCard] = useState(null);
  const [viewMode, setViewMode] = useState("auto");

  // Device detection
  const [deviceInfo, setDeviceInfo] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    isMobile: window.innerWidth < 768,
    isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
    isDesktop: window.innerWidth >= 1024,
    isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
    isAndroid: /Android/.test(navigator.userAgent),
    touchDevice: "ontouchstart" in window,
  });

  const containerRef = useRef(null);

  const [stats, setStats] = useState({
    total: 0,
    withDisability: 0,
    withInsurance: 0,
    withChronicDisease: 0,
    insuranceRate: 0,
  });

  const [form, setForm] = useState({
    afite_ubumuga: 0,
    ubwoko_bw_ubumuga: "",
    indwara_y_igihe_kirekire: "",
    afite_mutuelle: 0,
    ubwoko_bwa_mutuelle: "",
    ivuriro_ajyamo: "",
    amazina_y_umuturage: "",
    citizen_id: "",
  });

  // Citizens already registered under Abaturage, for the auto-populate
  // dropdown instead of hand-typing a name.
  const [citizens, setCitizens] = useState([]);
  useEffect(() => {
    axios.get(`${getApiBase()}/isibo/citizens`)
      .then((res) => setCitizens(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCitizens([]));
  }, []);

  const [formErrors, setFormErrors] = useState({});
  const [editId, setEditId] = useState(null);

  const insuranceTypes = [
    { value: "Mutuelle de Santé", label: "Mutuelle de Santé", color: "primary" },
    { value: "RAMA", label: "RAMA", color: "success" },
    { value: "MMI", label: "MMI", color: "info" },
    { value: "Radiant Yacu Ltd", label: "Radiant Yacu Ltd", color: "info" },
    { value: "Radiant Health Insurance", label: "Radiant Health Insurance", color: "info" },
    { value: "Sanlam Health Insurance", label: "Sanlam Health Insurance", color: "info" },
    { value: "SONARWA Health / Life Insurance", label: "SONARWA", color: "info" },
    { value: "PRIME Health Insurance", label: "PRIME", color: "info" },
    { value: "Britam Health Insurance", label: "Britam", color: "info" },
    { value: "Old Mutual Health Insurance", label: "Old Mutual", color: "info" },
    { value: "Privée", label: "Ubwishingizi bwite", color: "warning" },
  ];

  // ════════════════════════════════════════════════════════════════════════
  // THEME — Government Design System
  // ════════════════════════════════════════════════════════════════════════
  const theme = {
    bg: "#f8fafc",
    bgSecondary: "#f1f5f9",
    card: "#ffffff",
    cardHover: "#f8fafc",
    text: "#374151",
    textBright: "#111827",
    muted: "#6b7280",
    primary: "#1e3a5f",
    primaryGlow: "rgba(30, 58, 95, 0.15)",
    success: "#16a34a",
    successGlow: "rgba(22, 163, 74, 0.15)",
    warning: "#d97706",
    warningGlow: "rgba(217, 119, 6, 0.15)",
    danger: "#dc2626",
    dangerGlow: "rgba(220, 38, 38, 0.15)",
    info: "#1d4ed8",
    infoGlow: "rgba(29, 78, 216, 0.15)",
    accent: "#f59e0b",
    border: "#e5e7eb",
    borderLight: "#d1d5db",
    gradient1: "linear-gradient(135deg, #1e3a5f 0%, #2d5a8e 100%)",
    gradient2: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
    gradient3: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
    gradient4: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
  };

  // ════════════════════════════════════════════════════════════════════════
  // DEVICE DETECTION & RESPONSIVE HANDLER
  // ════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const updateDeviceInfo = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      setDeviceInfo({
        width,
        height,
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024,
        isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
        isAndroid: /Android/.test(navigator.userAgent),
        touchDevice: "ontouchstart" in window,
        isSmallPhone: width < 360,
        isMediumPhone: width >= 360 && width < 400,
        isLargePhone: width >= 400 && width < 480,
        isPhablet: width >= 480 && width < 768,
        isLandscape: width > height,
      });

      // Auto adjust items per page
      if (width < 360) {
        setItemsPerPage(5);
      } else if (width < 576) {
        setItemsPerPage(6);
      } else if (width < 768) {
        setItemsPerPage(8);
      } else if (width < 1024) {
        setItemsPerPage(10);
      } else {
        setItemsPerPage(12);
      }
    };

    updateDeviceInfo();
    window.addEventListener("resize", updateDeviceInfo);
    window.addEventListener("orientationchange", () => setTimeout(updateDeviceInfo, 100));

    return () => {
      window.removeEventListener("resize", updateDeviceInfo);
      window.removeEventListener("orientationchange", updateDeviceInfo);
    };
  }, []);

  // ════════════════════════════════════════════════════════════════════════
  // DATA FUNCTIONS
  // ════════════════════════════════════════════════════════════════════════
  const calculateStats = useCallback((data) => {
    const total = data.length;
    const withDisability = data.filter((item) => item.afite_ubumuga === 1).length;
    const withInsurance = data.filter((item) => item.afite_mutuelle === 1).length;
    const withChronicDisease = data.filter(
      (item) => item.indwara_y_igihe_kirekire && item.indwara_y_igihe_kirekire.trim() !== ""
    ).length;
    const insuranceRate = total > 0 ? ((withInsurance / total) * 100).toFixed(1) : 0;
    setStats({ total, withDisability, withInsurance, withChronicDisease, insuranceRate });
  }, []);

  const showAlertMessage = useCallback((msg, variant = "success") => {
    setAlert({ show: true, message: msg, variant });
    setTimeout(() => setAlert({ show: false, message: "", variant: "" }), 4500);
  }, []);

  const fetchUbuzima = useCallback(async () => {
    setLoading(true);
    try {
      const url = isUmudugudu
        ? `${getApiBase()}/umudugudu/module-data/ubuzima`
        : API_URL;
      const res = await axios.get(url);
      const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.ubuzima ?? []);
      setUbuzima(data);
      setFilteredData(data);
      calculateStats(data);
    } catch {
      showAlertMessage("Habaye ikosa mu kugira amakuru y'ubuzima", "danger");
    } finally {
      setLoading(false);
    }
  }, [calculateStats, showAlertMessage, isUmudugudu]);

  useEffect(() => {
    fetchUbuzima();
  }, [fetchUbuzima]);

  useEffect(() => {
    let result = [...ubuzima];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (item) =>
          item.amazina_y_umuturage?.toLowerCase().includes(term) ||
          item.ubwoko_bw_ubumuga?.toLowerCase().includes(term) ||
          item.indwara_y_igihe_kirekire?.toLowerCase().includes(term) ||
          item.ivuriro_ajyamo?.toLowerCase().includes(term) ||
          item.ubwoko_bwa_mutuelle?.toLowerCase().includes(term)
      );
    }
    switch (filterType) {
      case "disability":
        result = result.filter((item) => item.afite_ubumuga === 1);
        break;
      case "insurance":
        result = result.filter((item) => item.afite_mutuelle === 1);
        break;
      case "chronic":
        result = result.filter((item) => item.indwara_y_igihe_kirekire?.trim());
        break;
      case "no-insurance":
        result = result.filter((item) => item.afite_mutuelle === 0);
        break;
      default:
        break;
    }
    setFilteredData(result);
    setCurrentPage(1);
  }, [searchTerm, filterType, ubuzima]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  // ════════════════════════════════════════════════════════════════════════
  // FORM HANDLERS
  // ════════════════════════════════════════════════════════════════════════
  const validateForm = () => {
    const errors = {};
    if (!form.amazina_y_umuturage?.trim()) errors.amazina_y_umuturage = "Amazina ni ngombwa";
    if (form.afite_ubumuga === 1 && !form.ubwoko_bw_ubumuga?.trim())
      errors.ubwoko_bw_ubumuga = "Sobanura ubwoko bw'ubumuga";
    if (form.afite_mutuelle === 1 && !form.ubwoko_bwa_mutuelle?.trim())
      errors.ubwoko_bwa_mutuelle = "Hitamo ubwoko bw'ubwishingizi";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? (checked ? 1 : 0) : value });
    if (formErrors[name]) setFormErrors({ ...formErrors, [name]: "" });
  };

  const handleCitizenSelect = (e) => {
    const citizenId = e.target.value;
    const citizen = citizens.find((c) => String(c.umuturage_id) === String(citizenId));
    setForm((prev) => ({
      ...prev,
      citizen_id: citizenId,
      amazina_y_umuturage: citizen ? citizen.full_name : "",
    }));
    if (formErrors.amazina_y_umuturage) setFormErrors((prev) => ({ ...prev, amazina_y_umuturage: "" }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) {
      showAlertMessage("Uzuza neza amakuru yose akenewe!", "warning");
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API_URL}/${editId}`, form);
        showAlertMessage("Amakuru yavuguruwe neza!", "success");
      } else {
        await axios.post(API_URL, form);
        showAlertMessage("Amakuru yashyizwemo neza!", "success");
      }
      resetForm();
      setShow(false);
      fetchUbuzima();
    } catch {
      showAlertMessage("Ntibikunze kubika! Gerageza ukundi.", "danger");
    }
  };

  const resetForm = () => {
    setForm({
      afite_ubumuga: 0,
      ubwoko_bw_ubumuga: "",
      indwara_y_igihe_kirekire: "",
      afite_mutuelle: 0,
      ubwoko_bwa_mutuelle: "",
      ivuriro_ajyamo: "",
      amazina_y_umuturage: "",
      citizen_id: "",
    });
    setFormErrors({});
    setEditing(false);
    setEditId(null);
  };

  const handleAdd = () => {
    resetForm();
    setShow(true);
  };

  const handleEdit = (item) => {
    setForm({ ...item });
    setEditId(item.ubuzima_id);
    setEditing(true);
    setFormErrors({});
    setShow(true);
  };

  const handleViewDetails = (item) => {
    setSelectedItem(item);
    setShowDetails(true);
  };

  const handleDeleteClick = (item) => {
    setDeleteTarget(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/${deleteTarget.ubuzima_id}`);
      showAlertMessage("Amakuru yasibwe neza!", "success");
      fetchUbuzima();
    } catch {
      showAlertMessage("Ntibikunze gusiba!", "danger");
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilterType("all");
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  const exportToCSV = () => {
    const headers = [
      "No",
      "Amazina",
      "Afite Ubumuga",
      "Ubwoko bw'Ubumuga",
      "Indwara",
      "Afite Mutuelle",
      "Ubwoko bw'Ubwishingizi",
      "Ivuriro",
    ];
    const csvData = filteredData.map((item, index) => [
      index + 1,
      `"${item.amazina_y_umuturage || ""}"`,
      item.afite_ubumuga ? "Yego" : "Oya",
      `"${item.ubwoko_bw_ubumuga || "-"}"`,
      `"${item.indwara_y_igihe_kirekire || "-"}"`,
      item.afite_mutuelle ? "Yego" : "Oya",
      `"${item.ubwoko_bwa_mutuelle || "-"}"`,
      `"${item.ivuriro_ajyamo || "-"}"`,
    ]);
    const csv = [headers.join(","), ...csvData.map((row) => row.join(","))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `ubuzima_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    showAlertMessage("Dosiye yakoboye neza!", "success");
  };

  // ════════════════════════════════════════════════════════════════════════
  // RENDER HELPERS
  // ════════════════════════════════════════════════════════════════════════
  const getFilterLabel = () => {
    const labels = {
      all: "Byose",
      disability: "Bafite Ubumuga",
      insurance: "Bafite Mutuelle",
      chronic: "Indwara Zirambye",
      "no-insurance": "Nta Mutuelle",
    };
    return labels[filterType] || "Byose";
  };

  const StatusBadge = ({ value, type }) => {
    const isYes = value === 1 || value === true;

    const cfg = {
      disability: {
        yes: { bg: "#fff1f2", color: "#b91c1c", border: "#fecaca", icon: "exclamation-circle-fill" },
        no:  { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0", icon: "check-circle-fill" },
      },
      insurance: {
        yes: { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0", icon: "shield-fill-check" },
        no:  { bg: "#fff1f2", color: "#b91c1c", border: "#fecaca", icon: "shield-fill-x" },
      },
    };
    const palette = (cfg[type] || cfg.insurance)[isYes ? "yes" : "no"];
    const text = isYes ? "Yego" : "Oya";

    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.3rem",
          padding: "0.28rem 0.65rem",
          borderRadius: "999px",
          fontSize: "0.72rem",
          fontWeight: 700,
          letterSpacing: "0.01em",
          color: palette.color,
          background: palette.bg,
          border: `1px solid ${palette.border}`,
          whiteSpace: "nowrap",
          lineHeight: 1.4,
        }}
      >
        <i className={`bi bi-${palette.icon}`} style={{ fontSize: "0.7rem" }}></i>
        {text}
      </span>
    );
  };

  // ════════════════════════════════════════════════════════════════════════
  // STAT CARDS DATA
  // ════════════════════════════════════════════════════════════════════════
  const statCards = useMemo(
    () => [
      {
        title: deviceInfo.width < 400 ? "Bose" : "Abaturage Bose",
        val: stats.total,
        icon: "people-fill",
        iconBg: "#eff6ff",
        iconColor: "#2563eb",
        barColor: "#3b82f6",
        badgeBg: "#eff6ff",
        badgeColor: "#1d4ed8",
        badgeBorder: "#bfdbfe",
        percent: 100,
      },
      {
        title: deviceInfo.width < 400 ? "Mutuelle" : "Bafite Mutuelle",
        val: stats.withInsurance,
        icon: "shield-check-fill",
        iconBg: "#f0fdf4",
        iconColor: "#16a34a",
        barColor: "#22c55e",
        badgeBg: "#f0fdf4",
        badgeColor: "#15803d",
        badgeBorder: "#bbf7d0",
        percent: parseFloat(stats.insuranceRate) || 0,
      },
      {
        title: deviceInfo.width < 400 ? "Ubumuga" : "Bafite Ubumuga",
        val: stats.withDisability,
        icon: "person-wheelchair",
        iconBg: "#fff1f2",
        iconColor: "#dc2626",
        barColor: "#ef4444",
        badgeBg: "#fff1f2",
        badgeColor: "#b91c1c",
        badgeBorder: "#fecaca",
        percent: stats.total ? Math.round((stats.withDisability / stats.total) * 100) : 0,
      },
      {
        title: deviceInfo.width < 400 ? "Indwara" : "Indwara Zirambye",
        val: stats.withChronicDisease,
        icon: "thermometer-half",
        iconBg: "#fffbeb",
        iconColor: "#d97706",
        barColor: "#f59e0b",
        badgeBg: "#fffbeb",
        badgeColor: "#b45309",
        badgeBorder: "#fde68a",
        percent: stats.total ? Math.round((stats.withChronicDisease / stats.total) * 100) : 0,
      },
    ],
    [stats, deviceInfo.width]
  );

  // ════════════════════════════════════════════════════════════════════════
  // RENDER MOBILE CARDS (< 576px)
  // ════════════════════════════════════════════════════════════════════════
  const renderMobileCards = () => (
    <div className="mobile-cards-grid">
      {paginatedData.map((item, index) => {
        const isExpanded = expandedCard === item.ubuzima_id;
        return (
          <div key={item.ubuzima_id} className="mobile-health-card">
            {/* Card Header */}
            <div
              className="mhc-header"
              onClick={() => setExpandedCard(isExpanded ? null : item.ubuzima_id)}
            >
              <div className="mhc-header-left">
                <span className="mhc-number">#{startIndex + index + 1}</span>
                <div className="mhc-avatar">
                  <i className="bi bi-person-fill"></i>
                </div>
              </div>
              <div className="mhc-header-center">
                <h6 className="mhc-name">{item.amazina_y_umuturage}</h6>
                <div className="mhc-badges">
                  <StatusBadge value={item.afite_mutuelle} type="insurance" />
                  {item.afite_ubumuga === 1 && <StatusBadge value={1} type="disability" />}
                </div>
              </div>
              <div className="mhc-expand-icon">
                <i className={`bi bi-chevron-${isExpanded ? "up" : "down"}`}></i>
              </div>
            </div>

            {/* Card Body - Always Visible Summary */}
            <div className="mhc-body">
              <div className="mhc-summary-grid">
                <div className="mhc-summary-item">
                  <span className="mhc-label">
                    <i className="bi bi-shield"></i> Mutuelle
                  </span>
                  <span className="mhc-value">
                    {item.afite_mutuelle ? item.ubwoko_bwa_mutuelle || "Yego" : "Oya"}
                  </span>
                </div>
                <div className="mhc-summary-item">
                  <span className="mhc-label">
                    <i className="bi bi-hospital"></i> Ivuriro
                  </span>
                  <span className="mhc-value">{item.ivuriro_ajyamo || "—"}</span>
                </div>
              </div>
            </div>

            {/* Expanded Content */}
            <Collapse in={isExpanded}>
              <div className="mhc-expanded">
                <div className="mhc-detail-grid">
                  <div className="mhc-detail-item">
                    <span className="mhc-detail-label">Ubumuga</span>
                    <span className="mhc-detail-value">
                      {item.afite_ubumuga ? (
                        <>
                          <StatusBadge value={1} type="disability" />
                          {item.ubwoko_bw_ubumuga && (
                            <span className="mhc-detail-sub">{item.ubwoko_bw_ubumuga}</span>
                          )}
                        </>
                      ) : (
                        <StatusBadge value={0} type="disability" />
                      )}
                    </span>
                  </div>
                  <div className="mhc-detail-item">
                    <span className="mhc-detail-label">Indwara Irambye</span>
                    <span className="mhc-detail-value">
                      {item.indwara_y_igihe_kirekire || "—"}
                    </span>
                  </div>
                </div>
              </div>
            </Collapse>

            {/* Card Actions */}
            <div className="mhc-actions">
              <button className="mhc-action-btn view" onClick={() => handleViewDetails(item)}>
                <i className="bi bi-eye"></i>
                <span>Reba</span>
              </button>
              <button className="mhc-action-btn edit" onClick={() => handleEdit(item)}>
                <i className="bi bi-pencil"></i>
                <span>Hindura</span>
              </button>
              <button className="mhc-action-btn delete" onClick={() => handleDeleteClick(item)}>
                <i className="bi bi-trash"></i>
                <span>Siba</span>
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════
  // RENDER TABLET LIST (576px - 767px)
  // ════════════════════════════════════════════════════════════════════════
  const renderTabletList = () => (
    <div className="tablet-list-view">
      {paginatedData.map((item, index) => (
        <div key={item.ubuzima_id} className="tablet-list-item">
          <div className="tli-left">
            <span className="tli-number">{startIndex + index + 1}</span>
            <div className="tli-avatar">
              <i className="bi bi-person-fill"></i>
            </div>
          </div>

          <div className="tli-content">
            <div className="tli-header">
              <h6 className="tli-name">{item.amazina_y_umuturage}</h6>
              <div className="tli-badges">
                <StatusBadge value={item.afite_mutuelle} type="insurance" />
                {item.afite_ubumuga === 1 && <StatusBadge value={1} type="disability" />}
                {item.indwara_y_igihe_kirekire && (
                  <span className="tli-chronic-badge">
                    <i className="bi bi-thermometer-half"></i>
                  </span>
                )}
              </div>
            </div>

            <div className="tli-details">
              <span className="tli-detail">
                <i className="bi bi-shield-check"></i>
                {item.ubwoko_bwa_mutuelle || "Nta Mutuelle"}
              </span>
              {item.ivuriro_ajyamo && (
                <span className="tli-detail">
                  <i className="bi bi-hospital"></i>
                  {item.ivuriro_ajyamo}
                </span>
              )}
              {item.ubwoko_bw_ubumuga && (
                <span className="tli-detail">
                  <i className="bi bi-person-wheelchair"></i>
                  {item.ubwoko_bw_ubumuga}
                </span>
              )}
            </div>
          </div>

          <div className="tli-actions">
            <button className="tli-btn view" onClick={() => handleViewDetails(item)}>
              <i className="bi bi-eye"></i>
            </button>
            <button className="tli-btn edit" onClick={() => handleEdit(item)}>
              <i className="bi bi-pencil"></i>
            </button>
            <button className="tli-btn delete" onClick={() => handleDeleteClick(item)}>
              <i className="bi bi-trash"></i>
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════
  // RENDER DESKTOP TABLE (768px+)
  // ════════════════════════════════════════════════════════════════════════
  const renderDesktopTable = () => (
    <div className="table-responsive-wrapper">
      <table className="modern-health-table">
        <thead>
          <tr>
            <th className="th-number">#</th>
            <th className="th-name">Amazina</th>
            <th className="th-disability">Ubumuga</th>
            <th className="th-disability-type">Ubwoko bw'Ubumuga</th>
            <th className="th-chronic">Indwara</th>
            <th className="th-insurance">Mutuelle</th>
            <th className="th-insurance-type">Ubwoko bwa Mutuelle</th>
            <th className="th-hospital">Ivuriro</th>
            <th className="th-actions">Ibikorwa</th>
          </tr>
        </thead>
        <tbody>
          {paginatedData.map((item, index) => (
            <tr key={item.ubuzima_id}>
              <td className="td-number" style={{ overflow: "visible" }}>
                <span className="row-number">{startIndex + index + 1}</span>
              </td>
              <td className="td-name" style={{ overflow: "visible" }}>
                <div className="name-cell">
                  <div className="name-avatar">
                    <i className="bi bi-person-fill"></i>
                  </div>
                  <span className="name-text">{item.amazina_y_umuturage}</span>
                </div>
              </td>
              <td className="td-disability" style={{ overflow: "visible" }}>
                <StatusBadge value={item.afite_ubumuga} type="disability" />
              </td>
              <td>
                <span className="cell-text">{item.ubwoko_bw_ubumuga || "—"}</span>
              </td>
              <td>
                <span className="cell-text">{item.indwara_y_igihe_kirekire || "—"}</span>
              </td>
              <td className="td-insurance" style={{ overflow: "visible" }}>
                <StatusBadge value={item.afite_mutuelle} type="insurance" />
              </td>
              <td>
                <span className="cell-text">{item.ubwoko_bwa_mutuelle || "—"}</span>
              </td>
              <td>
                <span className="cell-text">{item.ivuriro_ajyamo || "—"}</span>
              </td>
              <td className="td-actions" style={{ overflow: "visible" }}>
                <div className="action-btns">
                  <OverlayTrigger placement="top" overlay={<Tooltip>Reba</Tooltip>}>
                    <button className="action-btn view" onClick={() => handleViewDetails(item)}>
                      <i className="bi bi-eye"></i>
                    </button>
                  </OverlayTrigger>
                  <OverlayTrigger placement="top" overlay={<Tooltip>Hindura</Tooltip>}>
                    <button className="action-btn edit" onClick={() => handleEdit(item)}>
                      <i className="bi bi-pencil"></i>
                    </button>
                  </OverlayTrigger>
                  <OverlayTrigger placement="top" overlay={<Tooltip>Siba</Tooltip>}>
                    <button className="action-btn delete" onClick={() => handleDeleteClick(item)}>
                      <i className="bi bi-trash"></i>
                    </button>
                  </OverlayTrigger>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════
  // RENDER DATA VIEW (Auto-select based on screen size)
  // ════════════════════════════════════════════════════════════════════════
  const renderDataView = () => {
    if (loading) {
      return (
        <div className="loading-state">
          <Spinner animation="border" style={{ color: theme.primary }} />
          <p>Gutegereza...</p>
        </div>
      );
    }

    if (filteredData.length === 0) {
      return (
        <div className="empty-state">
          <i className="bi bi-heart-pulse"></i>
          <h4>Nta makuru y'ubuzima ahari</h4>
          <p>
            {searchTerm || filterType !== "all"
              ? "Nta makuru ahuye n'ibyo ushakisha"
              : "Tangira ukongeramo amakuru mashya"}
          </p>
          <Button className="btn-primary-custom" onClick={handleAdd}>
            <i className="bi bi-plus-circle me-2"></i>
            Ongeraho Amakuru
          </Button>
        </div>
      );
    }

    const { width } = deviceInfo;

    if (viewMode === "cards" || (viewMode === "auto" && width < 576)) {
      return renderMobileCards();
    } else if (viewMode === "list" || (viewMode === "auto" && width >= 576 && width < 768)) {
      return renderTabletList();
    } else {
      return renderDesktopTable();
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // RENDER PAGINATION
  // ════════════════════════════════════════════════════════════════════════
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const { width } = deviceInfo;
    const maxVisible = width < 400 ? 3 : width < 576 ? 5 : 7;

    let pages = [];
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return (
      <div className="pagination-wrapper">
        <div className="pagination-info">
          <span>
            {width < 400
              ? `${startIndex + 1}-${Math.min(startIndex + itemsPerPage, filteredData.length)}`
              : `Igaragaza ${startIndex + 1} - ${Math.min(
                  startIndex + itemsPerPage,
                  filteredData.length
                )} kuri ${filteredData.length}`}
          </span>
        </div>

        <div className="pagination-controls">
          <button
            className="page-btn nav-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(1)}
          >
            <i className="bi bi-chevron-double-left"></i>
          </button>
          <button
            className="page-btn nav-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          >
            <i className="bi bi-chevron-left"></i>
          </button>

          {start > 1 && (
            <>
              <button className="page-btn" onClick={() => setCurrentPage(1)}>
                1
              </button>
              {start > 2 && <span className="page-ellipsis">...</span>}
            </>
          )}

          {pages.map((page) => (
            <button
              key={page}
              className={`page-btn ${page === currentPage ? "active" : ""}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}

          {end < totalPages && (
            <>
              {end < totalPages - 1 && <span className="page-ellipsis">...</span>}
              <button className="page-btn" onClick={() => setCurrentPage(totalPages)}>
                {totalPages}
              </button>
            </>
          )}

          <button
            className="page-btn nav-btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          >
            <i className="bi bi-chevron-right"></i>
          </button>
          <button
            className="page-btn nav-btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(totalPages)}
          >
            <i className="bi bi-chevron-double-right"></i>
          </button>
        </div>
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div
      ref={containerRef}
      className={`app-container ${deviceInfo.isIOS ? "ios" : ""} ${
        deviceInfo.isAndroid ? "android" : ""
      }`}
      style={{ background: theme.bg, color: theme.text }}
    >
      {/* ══════════════════════════════════════════════════════════════════
          COMPREHENSIVE RESPONSIVE STYLES
      ══════════════════════════════════════════════════════════════════ */}
      <style>{`
        /* ═══════════════════════════════════════════════════════════════════
           CSS CUSTOM PROPERTIES
        ═══════════════════════════════════════════════════════════════════ */
        :root {
          --bg: #f8fafc;
          --bg-secondary: #f1f5f9;
          --card: #ffffff;
          --card-hover: #f8fafc;
          --text: #374151;
          --text-bright: #111827;
          --muted: #6b7280;
          --primary: #1e3a5f;
          --primary-glow: rgba(30, 58, 95, 0.15);
          --success: #16a34a;
          --success-glow: rgba(22, 163, 74, 0.15);
          --warning: #d97706;
          --warning-glow: rgba(217, 119, 6, 0.15);
          --danger: #dc2626;
          --danger-glow: rgba(220, 38, 38, 0.15);
          --info: #1d4ed8;
          --info-glow: rgba(29, 78, 216, 0.15);
          --accent: #f59e0b;
          --border: #e5e7eb;
          --border-light: #d1d5db;
          
          --radius-sm: 8px;
          --radius-md: 12px;
          --radius-lg: 16px;
          --radius-xl: 20px;
          
          --font-xs: clamp(0.65rem, 2vw, 0.75rem);
          --font-sm: clamp(0.75rem, 2.5vw, 0.875rem);
          --font-md: clamp(0.875rem, 3vw, 1rem);
          --font-lg: clamp(1rem, 3.5vw, 1.25rem);
          --font-xl: clamp(1.25rem, 4vw, 1.5rem);
          --font-2xl: clamp(1.5rem, 5vw, 2rem);
          
          --space-xs: clamp(0.25rem, 1vw, 0.5rem);
          --space-sm: clamp(0.5rem, 2vw, 0.75rem);
          --space-md: clamp(0.75rem, 3vw, 1rem);
          --space-lg: clamp(1rem, 4vw, 1.5rem);
          --space-xl: clamp(1.5rem, 5vw, 2rem);
        }

        /* ═══════════════════════════════════════════════════════════════════
           BASE & RESET
        ═══════════════════════════════════════════════════════════════════ */
        @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;500;600;700;800&display=swap');
        
        *, *::before, *::after {
          box-sizing: border-box;
          -webkit-tap-highlight-color: transparent;
        }

        html {
          -webkit-text-size-adjust: 100%;
          scroll-behavior: smooth;
        }

        body {
          font-family: 'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          
        }

        /* ═══════════════════════════════════════════════════════════════════
           APP CONTAINER
        ═══════════════════════════════════════════════════════════════════ */
        .app-container {
          min-height: 100vh;
          min-height: 100dvh;
          padding: var(--space-md);
          padding-top: max(var(--space-md), env(safe-area-inset-top));
          padding-bottom: max(var(--space-md), env(safe-area-inset-bottom));
          padding-left: max(var(--space-md), env(safe-area-inset-left));
          padding-right: max(var(--space-md), env(safe-area-inset-right));
          overflow-x: hidden;
        }

        .app-container.ios {
          -webkit-overflow-scrolling: touch;
        }

        /* ═══════════════════════════════════════════════════════════════════
           HEADER
        ═══════════════════════════════════════════════════════════════════ */
        .header-section {
          margin-bottom: var(--space-lg);
        }

        .header-top {
          display: flex;
          flex-direction: column;
          gap: var(--space-sm);
        }

        .header-title {
          font-size: var(--font-xl);
          font-weight: 700;
          color: var(--text-bright);
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin: 0;
          line-height: 1.3;
        }

        .header-title i {
          color: var(--danger);
        }

        .header-subtitle {
          font-size: var(--font-sm);
          color: var(--muted);
          margin: 0;
          line-height: 1.4;
        }

        .header-actions {
          display: flex;
          gap: var(--space-xs);
          flex-wrap: wrap;
          margin-top: var(--space-sm);
          align-items: center;
        }

        .header-actions .btn-primary-custom,
        .header-actions .btn-outline-custom {
          flex-shrink: 0;
        }

        /* ═══════════════════════════════════════════════════════════════════
           BUTTONS
        ═══════════════════════════════════════════════════════════════════ */
        .btn-primary-custom {
          background: linear-gradient(135deg, var(--primary) 0%, #5a52e0 100%);
          border: none;
          border-radius: var(--radius-md);
          padding: var(--space-sm) var(--space-md);
          font-weight: 600;
          font-size: var(--font-sm);
          color: white;
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          box-shadow: 0 4px 15px var(--primary-glow);
        }

        .btn-primary-custom:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px var(--primary-glow);
        }

        .btn-primary-custom:active {
          transform: scale(0.96);
        }

        .btn-outline-custom {
          background: transparent;
          border: 1px solid var(--border-light);
          border-radius: var(--radius-md);
          padding: var(--space-sm) var(--space-md);
          font-size: var(--font-sm);
          color: var(--text);
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
        }

        .btn-outline-custom:hover {
          border-color: var(--primary);
          color: var(--primary);
        }

        .btn-outline-custom.success {
          color: var(--success);
        }

        .btn-outline-custom.success:hover {
          border-color: var(--success);
          background: rgba(0, 214, 143, 0.1);
        }

        /* ═══════════════════════════════════════════════════════════════════
           STATS GRID
        ═══════════════════════════════════════════════════════════════════ */
        /* ═══════════════════════════════════════════════════════════════════
           STATS GRID
        ═══════════════════════════════════════════════════════════════════ */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
          margin-bottom: var(--space-lg);
          width: 100%;
        }

        .stat-card {
          background: #ffffff;
          border-radius: 18px;
          padding: 1.25rem;
          border: 1px solid #f0f0f0;
          position: relative;
          overflow: hidden;
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
          box-shadow: 0 1px 4px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.04);
          cursor: default;
        }

        .stat-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.08), 0 12px 32px rgba(0,0,0,0.07);
          border-color: #e2e8f0;
        }

        /* top row */
        .stat-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 1rem;
        }

        /* icon box */
        .stat-icon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.25s ease;
        }

        .stat-card:hover .stat-icon {
          transform: scale(1.1);
        }

        .stat-icon i {
          font-size: 1.25rem;
          color: white;
        }

        /* percent badge */
        .stat-percent {
          display: inline-flex;
          align-items: center;
          padding: 0.25rem 0.65rem;
          border-radius: 999px;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.01em;
          border: 1px solid transparent;
          white-space: nowrap;
          line-height: 1.4;
        }

        /* big number */
        .stat-value {
          font-size: clamp(1.75rem, 5vw, 2.25rem);
          font-weight: 800;
          color: #0f172a;
          line-height: 1;
          letter-spacing: -0.02em;
          margin-bottom: 0.3rem;
          font-variant-numeric: tabular-nums;
        }

        /* label */
        .stat-label {
          font-size: 0.8rem;
          color: #64748b;
          font-weight: 600;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          line-height: 1.3;
        }

        /* progress track */
        .stat-progress {
          margin-top: 0.9rem;
          height: 5px;
          border-radius: 999px;
          background: #f1f5f9;
          overflow: hidden;
        }

        /* progress fill */
        .stat-progress-bar {
          height: 100%;
          border-radius: 999px;
          transition: width 0.9s cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* ═══════════════════════════════════════════════════════════════════
           FILTERS
        ═══════════════════════════════════════════════════════════════════ */
        .filter-section {
          margin-bottom: var(--space-md);
        }

        .filter-card {
          background: var(--card);
          border-radius: var(--radius-lg);
          padding: var(--space-md);
          border: 1px solid var(--border);
        }

        .filter-row {
          display: flex;
          gap: var(--space-sm);
          flex-wrap: wrap;
          align-items: center;
        }

        .search-wrapper {
          flex: 1;
          min-width: min(200px, 100%);
          position: relative;
        }

        .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--muted);
          pointer-events: none;
        }

        .search-input {
          width: 100%;
          background: var(--bg-secondary) !important;
          border: 1px solid var(--border) !important;
          color: var(--text) !important;
          border-radius: var(--radius-md) !important;
          padding: var(--space-sm) var(--space-sm) var(--space-sm) 2.25rem !important;
          font-size: var(--font-sm) !important;
        }

        .search-input:focus {
          border-color: var(--primary) !important;
          box-shadow: 0 0 0 3px var(--primary-glow) !important;
        }

        .search-input::placeholder {
          color: var(--muted) !important;
          opacity: 0.7;
        }

        .filter-dropdown {
          min-width: 140px;
        }

        .filter-info {
          margin-top: var(--space-sm);
          font-size: var(--font-xs);
          color: var(--muted);
        }

        .mobile-filter-btn {
          display: none !important;
        }

        /* ═══════════════════════════════════════════════════════════════════
           DATA CONTAINER
        ═══════════════════════════════════════════════════════════════════ */
        .data-container {
          background: var(--card);
          border-radius: var(--radius-xl);
          border: 1px solid var(--border);
          overflow: hidden;
        }

        .data-header {
          background: linear-gradient(180deg, rgba(255,255,255,0.02) 0%, transparent 100%);
          padding: var(--space-md);
          border-bottom: 1px solid var(--border);
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: var(--space-sm);
          min-width: 0;
        }

        .data-title {
          font-size: var(--font-md);
          font-weight: 600;
          color: var(--text-bright);
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin: 0;
        }

        .data-title i {
          color: var(--primary);
        }

        .data-meta {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
        }

        .results-count {
          font-size: var(--font-xs);
          color: var(--muted);
        }

        .view-toggle {
          display: none;
          gap: 3px;
          background: var(--bg);
          padding: 3px;
          border-radius: var(--radius-sm);
        }

        .view-toggle-btn {
          background: transparent;
          border: none;
          color: var(--muted);
          padding: 0.35rem 0.5rem;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .view-toggle-btn.active {
          background: var(--primary);
          color: white;
        }

        /* ═══════════════════════════════════════════════════════════════════
           LOADING & EMPTY STATES
        ═══════════════════════════════════════════════════════════════════ */
        .loading-state,
        .empty-state {
          padding: var(--space-xl) var(--space-md);
          text-align: center;
        }

        .loading-state p,
        .empty-state h4 {
          color: var(--muted);
          margin-top: var(--space-md);
        }

        .empty-state i {
          font-size: clamp(3rem, 15vw, 5rem);
          color: var(--border);
        }

        .empty-state p {
          color: #52525b;
          margin-bottom: var(--space-lg);
        }

        /* ═══════════════════════════════════════════════════════════════════
           MOBILE CARDS (< 576px)
        ═══════════════════════════════════════════════════════════════════ */
        .mobile-cards-grid {
          padding: var(--space-sm);
          display: flex;
          flex-direction: column;
          gap: var(--space-sm);
        }

        .mobile-health-card {
          background: linear-gradient(145deg, var(--card) 0%, var(--card-hover) 100%);
          border: 1px solid var(--border);
          border-radius: var(--radius-lg);
          overflow: hidden;
        }

        .mhc-header {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          padding: var(--space-sm);
          background: rgba(255, 255, 255, 0.02);
          cursor: pointer;
        }

        .mhc-header-left {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
        }

        .mhc-number {
          font-size: 0.6rem;
          color: var(--muted);
          font-weight: 600;
        }

        .mhc-avatar {
          width: 36px;
          height: 36px;
          border-radius: var(--radius-sm);
          background: linear-gradient(135deg, var(--primary)33, var(--primary)11);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .mhc-avatar i {
          color: var(--primary);
          font-size: 1rem;
        }

        .mhc-header-center {
          flex: 1;
          min-width: 0;
        }

        .mhc-name {
          font-size: var(--font-sm);
          font-weight: 600;
          color: var(--text-bright);
          margin: 0 0 0.25rem 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .mhc-badges {
          display: flex;
          gap: 0.25rem;
          flex-wrap: wrap;
        }

        .mhc-expand-icon {
          color: var(--muted);
          font-size: 0.9rem;
          padding: 0.25rem;
        }

        .mhc-body {
          padding: var(--space-sm);
          border-top: 1px solid rgba(255, 255, 255, 0.05);
        }

        .mhc-summary-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: var(--space-sm);
        }

        .mhc-summary-item {
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
        }

        .mhc-label {
          font-size: var(--font-xs);
          color: var(--muted);
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .mhc-value {
          font-size: var(--font-sm);
          color: var(--text);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .mhc-expanded {
          padding: var(--space-sm);
          background: rgba(0, 0, 0, 0.2);
          border-top: 1px dashed var(--border);
        }

        .mhc-detail-grid {
          display: flex;
          flex-direction: column;
          gap: var(--space-sm);
        }

        .mhc-detail-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .mhc-detail-label {
          font-size: var(--font-xs);
          color: var(--muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .mhc-detail-value {
          font-size: var(--font-sm);
          color: var(--text);
        }

        .mhc-detail-sub {
          display: block;
          font-size: var(--font-xs);
          color: var(--muted);
          margin-top: 0.25rem;
        }

        .mhc-actions {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          border-top: 1px solid var(--border);
        }

        .mhc-action-btn {
          border: none;
          background: transparent;
          padding: var(--space-sm);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.2rem;
          cursor: pointer;
          transition: all 0.2s;
          font-size: var(--font-xs);
        }

        .mhc-action-btn i {
          font-size: 1rem;
        }

        .mhc-action-btn.view {
          color: var(--info);
          border-right: 1px solid var(--border);
        }

        .mhc-action-btn.view:hover,
        .mhc-action-btn.view:active {
          background: rgba(0, 149, 255, 0.1);
        }

        .mhc-action-btn.edit {
          color: var(--primary);
          border-right: 1px solid var(--border);
        }

        .mhc-action-btn.edit:hover,
        .mhc-action-btn.edit:active {
          background: rgba(108, 99, 255, 0.1);
        }

        .mhc-action-btn.delete {
          color: var(--danger);
        }

        .mhc-action-btn.delete:hover,
        .mhc-action-btn.delete:active {
          background: rgba(255, 61, 113, 0.1);
        }

        /* ═══════════════════════════════════════════════════════════════════
           TABLET LIST (576px - 767px)
        ═══════════════════════════════════════════════════════════════════ */
        .tablet-list-view {
          padding: var(--space-sm);
        }

        .tablet-list-item {
          display: flex;
          gap: var(--space-sm);
          padding: var(--space-sm);
          background: linear-gradient(145deg, var(--card) 0%, var(--card-hover) 100%);
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          margin-bottom: var(--space-xs);
          transition: all 0.2s;
        }

        .tablet-list-item:hover {
          border-color: var(--primary);
          background: var(--card-hover);
        }

        .tli-left {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
        }

        .tli-number {
          font-size: 0.65rem;
          color: var(--muted);
          font-weight: 600;
        }

        .tli-avatar {
          width: 40px;
          height: 40px;
          border-radius: var(--radius-sm);
          background: linear-gradient(135deg, var(--primary)33, var(--primary)11);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .tli-avatar i {
          color: var(--primary);
          font-size: 1.1rem;
        }

        .tli-content {
          flex: 1;
          min-width: 0;
        }

        .tli-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: var(--space-xs);
          margin-bottom: 0.35rem;
        }

        .tli-name {
          font-size: var(--font-sm);
          font-weight: 600;
          color: var(--text-bright);
          margin: 0;
        }

        .tli-badges {
          display: flex;
          gap: 0.25rem;
          flex-wrap: wrap;
        }

        .tli-chronic-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: rgba(255, 170, 0, 0.12);
          color: var(--warning);
          font-size: 0.75rem;
        }

        .tli-details {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-sm);
        }

        .tli-detail {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: var(--font-xs);
          color: var(--muted);
        }

        .tli-detail i {
          font-size: 0.8em;
          opacity: 0.7;
        }

        .tli-actions {
          display: flex;
          flex-direction: column;
          gap: var(--space-xs);
        }

        .tli-btn {
          width: 36px;
          height: 36px;
          border-radius: var(--radius-sm);
          border: 1px solid;
          background: transparent;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .tli-btn.view {
          border-color: rgba(0, 149, 255, 0.3);
          color: var(--info);
        }

        .tli-btn.view:hover {
          background: rgba(0, 149, 255, 0.1);
        }

        .tli-btn.edit {
          border-color: rgba(108, 99, 255, 0.3);
          color: var(--primary);
        }

        .tli-btn.edit:hover {
          background: rgba(108, 99, 255, 0.1);
        }

        .tli-btn.delete {
          border-color: rgba(255, 61, 113, 0.3);
          color: var(--danger);
        }

        .tli-btn.delete:hover {
          background: rgba(255, 61, 113, 0.1);
        }

        /* ═══════════════════════════════════════════════════════════════════
           DESKTOP TABLE (768px+)
        ═══════════════════════════════════════════════════════════════════ */
        .table-responsive-wrapper {
          overflow-x: auto;
          overflow-y: visible;
          -webkit-overflow-scrolling: touch;
          /* styled scrollbar */
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f8fafc;
        }

        .table-responsive-wrapper::-webkit-scrollbar {
          height: 6px;
        }
        .table-responsive-wrapper::-webkit-scrollbar-track {
          background: #f8fafc;
          border-radius: 3px;
        }
        .table-responsive-wrapper::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        .table-responsive-wrapper::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        .modern-health-table {
          width: 100%;
          min-width: 720px;          /* prevents column crush; triggers scroll */
          border-collapse: separate;
          border-spacing: 0;
          table-layout: fixed;
        }

        /* ── column widths ── */
        .th-number        { width: 52px; }
        .th-name          { width: 200px; }
        .th-disability    { width: 90px; }
        .th-disability-type { width: 140px; }
        .th-chronic       { width: 150px; }
        .th-insurance     { width: 90px; }
        .th-insurance-type { width: 160px; }
        .th-hospital      { width: 140px; }
        .th-actions       { width: 110px; }

        /* ── sticky light header ── */
        .modern-health-table thead tr {
          background: #f8fafc;
        }

        .modern-health-table th {
          color: #475569;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          font-size: 0.68rem;
          padding: 0.85rem 1rem;
          border-top: 1px solid #e2e8f0;
          border-bottom: 2px solid #e2e8f0;
          text-align: left;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          position: sticky;
          top: 0;
          z-index: 2;
          background: #f8fafc;
        }

        .modern-health-table th.th-number,
        .modern-health-table th.th-disability,
        .modern-health-table th.th-insurance,
        .modern-health-table th.th-actions {
          text-align: center;
        }

        /* ── rows ── */
        .modern-health-table tbody tr {
          transition: background 0.15s ease;
        }

        .modern-health-table tbody tr:nth-child(odd) {
          background: #ffffff;
        }

        .modern-health-table tbody tr:nth-child(even) {
          background: #f8fafc;
        }

        .modern-health-table tbody tr:hover {
          background: #eff6ff;
        }

        .modern-health-table td {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #f1f5f9;
          vertical-align: middle;
          /* prevent text from breaking into vertical letters */
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 0;            /* required for text-overflow on table cells */
          font-size: 0.85rem;
          color: #374151;
        }

        /* ── number chip ── */
        .td-number { text-align: center; }

        .row-number {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 8px;
          background: #eff6ff;
          color: #1d4ed8;
          font-size: 0.72rem;
          font-weight: 700;
          flex-shrink: 0;
        }

        /* ── name cell ── */
        .name-cell {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          min-width: 0;
        }

        .name-avatar {
          width: 34px;
          height: 34px;
          border-radius: 10px;
          background: #eff6ff;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .name-avatar i {
          color: #2563eb;
          font-size: 1rem;
        }

        .name-text {
          font-weight: 600;
          color: #0f172a;
          font-size: 0.85rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          min-width: 0;
        }

        /* ── generic cell text ── */
        .cell-text {
          color: #475569;
          font-size: 0.82rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: block;
        }

        /* ── badge columns ── */
        .td-disability,
        .td-insurance {
          text-align: center;
        }

        /* ── actions ── */
        .td-actions { text-align: center; }

        .action-btns {
          display: flex;
          gap: 0.3rem;
          justify-content: center;
          align-items: center;
        }

        .action-btn {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: 1px solid;
          background: transparent;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.15s ease, transform 0.15s ease;
          font-size: 0.85rem;
          flex-shrink: 0;
        }

        .action-btn:hover { transform: translateY(-1px); }

        .action-btn.view {
          border-color: #bfdbfe;
          background: #eff6ff;
          color: #1d4ed8;
        }
        .action-btn.view:hover { background: #dbeafe; }

        .action-btn.edit {
          border-color: #bbf7d0;
          background: #f0fdf4;
          color: #15803d;
        }
        .action-btn.edit:hover { background: #dcfce7; }

        .action-btn.delete {
          border-color: #fecaca;
          background: #fff1f2;
          color: #b91c1c;
        }
        .action-btn.delete:hover { background: #fee2e2; }

        /* ═══════════════════════════════════════════════════════════════════
           PAGINATION
        ═══════════════════════════════════════════════════════════════════ */
        .pagination-wrapper {
          padding: var(--space-md);
          border-top: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: var(--space-sm);
        }

        .pagination-info {
          font-size: var(--font-xs);
          color: var(--muted);
        }

        .pagination-controls {
          display: flex;
          gap: 3px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .page-btn {
          min-width: 36px;
          height: 36px;
          border-radius: var(--radius-sm);
          border: 1px solid var(--border);
          background: transparent;
          color: var(--muted);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: var(--font-sm);
          font-weight: 500;
          transition: all 0.2s;
        }

        .page-btn:hover:not(:disabled) {
          background: rgba(108, 99, 255, 0.1);
          border-color: var(--primary);
          color: var(--primary);
        }

        .page-btn.active {
          background: var(--primary);
          border-color: var(--primary);
          color: white;
          font-weight: 700;
          box-shadow: 0 4px 15px var(--primary-glow);
        }

        .page-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .page-ellipsis {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 30px;
          color: var(--muted);
        }

        /* ═══════════════════════════════════════════════════════════════════
           MODALS
        ═══════════════════════════════════════════════════════════════════ */
        .modal-content {
          background: var(--bg) !important;
          border: 1px solid var(--border) !important;
          border-radius: var(--radius-xl) !important;
          color: var(--text) !important;
        }

        .modal-header {
          border-bottom: 1px solid var(--border) !important;
          padding: var(--space-md) !important;
        }

        .modal-header .btn-close {
          filter: invert(1) grayscale(100%) brightness(200%);
        }

        .modal-title {
          font-size: var(--font-lg) !important;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .modal-body {
          padding: var(--space-lg) !important;
        }

        .modal-footer {
          border-top: 1px solid var(--border) !important;
          padding: var(--space-md) !important;
          gap: var(--space-sm);
        }

        .form-control,
        .form-select {
          background: var(--card) !important;
          border: 1px solid var(--border) !important;
          color: var(--text) !important;
          border-radius: var(--radius-sm) !important;
          padding: var(--space-sm) !important;
          font-size: var(--font-sm) !important;
        }

        .form-control:focus,
        .form-select:focus {
          border-color: var(--primary) !important;
          box-shadow: 0 0 0 3px var(--primary-glow) !important;
        }

        .form-label {
          color: var(--text) !important;
          font-size: var(--font-sm) !important;
          font-weight: 600 !important;
          margin-bottom: 0.4rem !important;
        }

        .form-control::placeholder {
          color: var(--muted) !important;
          opacity: 0.6;
        }

        .form-select option {
          background: var(--card);
          color: var(--text);
        }

        .form-check-input:checked {
          background-color: var(--primary) !important;
          border-color: var(--primary) !important;
        }

        .form-check-label {
          color: var(--text) !important;
        }

        .section-divider {
          border-color: var(--border);
          margin: var(--space-md) 0;
        }

        .section-title {
          color: var(--muted);
          font-size: var(--font-sm);
          margin-bottom: var(--space-sm);
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        /* ═══════════════════════════════════════════════════════════════════
           DELETE MODAL
        ═══════════════════════════════════════════════════════════════════ */
        .delete-modal-content {
          background: var(--card);
          border-radius: var(--radius-xl);
          border: 1px solid var(--border);
          padding: var(--space-xl);
          text-align: center;
        }

        .delete-icon-wrapper {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: rgba(255, 61, 113, 0.1);
          border: 3px solid var(--danger);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto var(--space-lg);
          animation: pulse-danger 2s infinite;
        }

        .delete-icon-wrapper i {
          font-size: 2.2rem;
          color: var(--danger);
        }

        .delete-title {
          color: var(--text-bright);
          font-weight: 700;
          font-size: var(--font-lg);
          margin-bottom: var(--space-sm);
        }

        .delete-message {
          color: var(--muted);
          font-size: var(--font-sm);
          line-height: 1.6;
        }

        .delete-target-box {
          background: rgba(255, 61, 113, 0.08);
          border: 1px solid rgba(255, 61, 113, 0.2);
          border-radius: var(--radius-md);
          padding: var(--space-sm) var(--space-md);
          margin: var(--space-md) 0;
        }

        .delete-target-name {
          color: var(--text-bright);
          font-size: var(--font-md);
          font-weight: 600;
        }

        .delete-warning {
          font-size: var(--font-xs);
          color: var(--muted);
          margin-bottom: var(--space-lg);
        }

        .delete-actions {
          display: flex;
          gap: var(--space-sm);
          justify-content: center;
          flex-wrap: wrap;
        }

        @keyframes pulse-danger {
          0% { box-shadow: 0 0 0 0 rgba(255, 61, 113, 0.4); }
          70% { box-shadow: 0 0 0 15px rgba(255, 61, 113, 0); }
          100% { box-shadow: 0 0 0 0 rgba(255, 61, 113, 0); }
        }

        /* ═══════════════════════════════════════════════════════════════════
           OFFCANVAS
        ═══════════════════════════════════════════════════════════════════ */
        .offcanvas {
          background: var(--bg) !important;
          border-left: 1px solid var(--border) !important;
        }

        .offcanvas-header {
          border-bottom: 1px solid var(--border);
          padding: var(--space-md);
        }

        .offcanvas-title {
          color: var(--text-bright);
          font-weight: 600;
        }

        .offcanvas-body {
          padding: var(--space-md);
        }

        /* ═══════════════════════════════════════════════════════════════════
           ALERT
        ═══════════════════════════════════════════════════════════════════ */
        .alert {
          border-radius: var(--radius-md) !important;
          border: none !important;
        }

        /* ═══════════════════════════════════════════════════════════════════
           TOOLTIP
        ═══════════════════════════════════════════════════════════════════ */
        .tooltip-inner {
          background: var(--card) !important;
          color: var(--text) !important;
          border: 1px solid var(--border) !important;
          border-radius: var(--radius-sm) !important;
          font-size: var(--font-xs) !important;
          padding: 0.4rem 0.8rem !important;
        }

        .tooltip .tooltip-arrow::before {
          border-top-color: var(--card) !important;
        }

        /* ═══════════════════════════════════════════════════════════════════
           DROPDOWN
        ═══════════════════════════════════════════════════════════════════ */
        .dropdown-menu {
          background: var(--card) !important;
          border: 1px solid var(--border) !important;
          border-radius: var(--radius-md) !important;
          padding: 0.5rem !important;
        }

        .dropdown-item {
          color: var(--text) !important;
          border-radius: var(--radius-sm) !important;
          padding: 0.5rem 1rem !important;
          transition: all 0.15s ease !important;
        }

        .dropdown-item:hover {
          background: rgba(108, 99, 255, 0.1) !important;
          color: var(--primary) !important;
        }

        .dropdown-divider {
          border-color: var(--border) !important;
        }

        .btn-outline-light {
          border-color: var(--border-light) !important;
          color: var(--text) !important;
          border-radius: var(--radius-md) !important;
        }

        .btn-outline-light:hover {
          background: rgba(255, 255, 255, 0.05) !important;
          border-color: var(--primary) !important;
          color: var(--primary) !important;
        }

        /* ═══════════════════════════════════════════════════════════════════
           SCROLLBAR
        ═══════════════════════════════════════════════════════════════════ */
        ::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }

        ::-webkit-scrollbar-track {
          background: var(--bg);
        }

        ::-webkit-scrollbar-thumb {
          background: var(--border-light);
          border-radius: 3px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: var(--muted);
        }

        * {
          scrollbar-width: thin;
          scrollbar-color: var(--border-light) var(--bg);
        }

        /* ═══════════════════════════════════════════════════════════════════
           RESPONSIVE BREAKPOINTS
        ═══════════════════════════════════════════════════════════════════ */

        /* Extra Small (< 360px) */
        @media (max-width: 359px) {
          .app-container {
            padding: 0.5rem;
          }

          .stats-grid {
            gap: 0.35rem;
          }

          .stat-card {
            padding: 0.5rem;
          }

          .stat-value {
            font-size: 1.25rem;
          }

          .header-actions {
            width: 100%;
          }

          .header-actions .btn-primary-custom {
            flex: 1;
          }

          .mhc-actions {
            grid-template-columns: repeat(3, 1fr);
          }

          .mhc-action-btn span {
            font-size: 0.6rem;
          }

          .filter-row {
            flex-direction: column;
          }

          .filter-dropdown,
          .search-wrapper {
            width: 100%;
          }
        }

        /* Small Phones (360px - 479px) */
        @media (min-width: 360px) and (max-width: 479px) {
          .stat-value {
            font-size: 1.5rem;
          }
        }

        /* Medium Phones (480px - 575px) */
        @media (min-width: 480px) and (max-width: 575px) {
          .stats-grid {
            grid-template-columns: repeat(4, 1fr);
          }

          .stat-card {
            flex-direction: column;
            text-align: center;
            padding: var(--space-sm);
          }

          .stat-header {
            flex-direction: column;
            align-items: center;
            gap: var(--space-xs);
          }

          .stat-value {
            font-size: 1.25rem;
          }

          .stat-label {
            font-size: 0.55rem;
          }

          .mobile-cards-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
          }

          .mhc-actions {
            grid-template-columns: 1fr;
          }

          .mhc-action-btn {
            flex-direction: row;
          }
        }

        /* Mobile General (< 576px) */
        @media (max-width: 575px) {
          .mobile-filter-btn {
            display: inline-flex !important;
          }

          .desktop-filters {
            display: none !important;
          }

          .view-toggle {
            display: none;
          }

          .header-top {
            text-align: left;
          }

          .header-actions {
            justify-content: flex-start;
          }

          .data-title span {
            font-size: var(--font-sm);
          }

          .modal-body {
            padding: var(--space-md) !important;
          }
        }

        /* Tablets (576px - 767px) */
        @media (min-width: 576px) and (max-width: 767px) {
          .stats-grid {
            grid-template-columns: repeat(4, 1fr);
          }

          .mobile-filter-btn {
            display: inline-flex !important;
          }

          .desktop-filters {
            display: none !important;
          }

          .view-toggle {
            display: flex;
          }
        }

        /* Tablets and Up (768px+) */
        @media (min-width: 768px) {
          .app-container {
            padding: var(--space-lg);
          }

          .stats-grid {
            grid-template-columns: repeat(4, 1fr);
            gap: 1rem;
          }

          .header-top {
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
          }

          .header-actions {
            margin-top: 0;
            flex-shrink: 0;
          }

          .mobile-filter-btn {
            display: none !important;
          }

          .view-toggle {
            display: flex;
          }

          .filter-row {
            flex-wrap: nowrap;
            align-items: center;
          }

          .search-wrapper {
            flex: 1;
          }
        }

        /* Desktop (1024px+) */
        @media (min-width: 1024px) {
          .app-container {
            padding: var(--space-xl) calc(var(--space-xl) * 1.5);
          }

          .stat-value {
            font-size: 2rem;
          }

          .modern-health-table th,
          .modern-health-table td {
            padding: var(--space-md);
          }
        }

        /* Large Desktop (1440px+) */
        @media (min-width: 1440px) {
          .app-container {
            max-width: 1600px;
            margin: 0 auto;
          }

          .stat-value {
            font-size: 2.4rem;
          }
        }

        /* Touch Devices */
        @media (hover: none) and (pointer: coarse) {
          .action-btn,
          .tli-btn,
          .page-btn,
          .mhc-action-btn {
            min-width: 44px;
            min-height: 44px;
          }

          .stat-card:hover {
            transform: none;
          }

          .action-btn:hover {
            transform: none;
          }

          .btn-primary-custom:hover,
          .btn-outline-custom:hover {
            transform: none;
          }
        }

        /* Landscape Mobile */
        @media (max-width: 767px) and (orientation: landscape) {
          .stats-grid {
            grid-template-columns: repeat(4, 1fr);
          }

          .mobile-cards-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
          }
        }

        /* Reduced Motion */
        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            animation-duration: 0.01ms !important;
            transition-duration: 0.01ms !important;
          }
        }

        /* Print */
        @media print {
          .app-container {
            background: white !important;
            color: black !important;
          }

          .btn-primary-custom,
          .btn-outline-custom,
          .action-btns,
          .mhc-actions,
          .tli-actions,
          .pagination-wrapper,
          .header-actions,
          .mobile-filter-btn {
            display: none !important;
          }
        }
      `}</style>

      {/* Alert */}
      {alert.show && (
        <Alert
          variant={alert.variant}
          dismissible
          onClose={() => setAlert({ show: false })}
          className="mb-4"
        >
          {alert.message}
        </Alert>
      )}

      {/* Header */}
      <div className="header-section">
        <div className="header-top">
          <div>
            <h2 className="header-title">
              <i className="bi bi-heart-pulse-fill"></i>
              Ubuzima bw'Abaturage
            </h2>
            <p className="header-subtitle">
              {deviceInfo.width < 400
                ? "Gucunga amakuru y'ubuzima"
                : "Gucunga no gukurikirana amakuru y'ubuzima bw'abaturage"}
            </p>
          </div>

          <div className="header-actions">
            <button className="btn-primary-custom" onClick={handleAdd}>
              <i className="bi bi-plus-circle"></i>
              <span>Ongeraho</span>
            </button>

            <button className="btn-outline-custom" onClick={fetchUbuzima} title="Vugurura">
              <i className="bi bi-arrow-clockwise"></i>
            </button>

            <button className="btn-outline-custom success" onClick={exportToCSV}>
              <i className="bi bi-download"></i>
              <span className="d-none d-sm-inline">Excel</span>
            </button>

            <button
              className="btn-outline-custom mobile-filter-btn"
              onClick={() => setShowMobileFilters(true)}
              title="Filters"
            >
              <i className="bi bi-funnel"></i>
              {(searchTerm || filterType !== "all") && (
                <span
                  style={{
                    width: 8, height: 8, borderRadius: "50%",
                    background: "var(--primary)", display: "inline-block", marginLeft: 2
                  }}
                />
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="stats-grid">
        {statCards.map((s, i) => (
          <div key={i} className="stat-card">
            <div className="stat-header">
              <div className="stat-icon" style={{ background: s.iconBg }}>
                <i className={`bi bi-${s.icon}`} style={{ color: s.iconColor, fontSize: "1.25rem" }}></i>
              </div>
              <span
                className="stat-percent"
                style={{
                  background: s.badgeBg,
                  color: s.badgeColor,
                  borderColor: s.badgeBorder,
                }}
              >
                {s.percent}%
              </span>
            </div>
            <div className="stat-value">{s.val.toLocaleString()}</div>
            <div className="stat-label">{s.title}</div>
            <div className="stat-progress">
              <div
                className="stat-progress-bar"
                style={{ width: `${s.percent}%`, background: s.barColor }}
              ></div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Filters */}
      <div className="filter-section desktop-filters">
        <div className="filter-card">
          <div className="filter-row">
            <div className="search-wrapper">
              <i className="bi bi-search search-icon"></i>
              <input
                type="text"
                className="search-input form-control"
                placeholder="Shakisha amazina, ubumuga, indwara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="filter-dropdown">
              <DropdownButton
                variant="outline-light"
                title={
                  <>
                    <i className="bi bi-funnel me-1"></i>
                    {getFilterLabel()}
                  </>
                }
              >
                <Dropdown.Item onClick={() => setFilterType("all")}>Byose</Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => setFilterType("insurance")}>
                  <i className="bi bi-shield-check me-2 text-success"></i>Bafite Mutuelle
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setFilterType("no-insurance")}>
                  <i className="bi bi-shield-x me-2 text-danger"></i>Nta Mutuelle
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setFilterType("disability")}>
                  <i className="bi bi-person-wheelchair me-2 text-danger"></i>Bafite Ubumuga
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setFilterType("chronic")}>
                  <i className="bi bi-thermometer-half me-2 text-warning"></i>Indwara Zirambye
                </Dropdown.Item>
              </DropdownButton>
            </div>

            <button className="btn-outline-custom" onClick={resetFilters}>
              <i className="bi bi-arrow-repeat"></i>
              <span className="d-none d-lg-inline">Reset</span>
            </button>
          </div>

          {(searchTerm || filterType !== "all") && (
            <div className="filter-info">
              <i className="bi bi-info-circle me-1"></i>
              Byasanze: <strong>{filteredData.length}</strong> mu baturage{" "}
              <strong>{ubuzima.length}</strong>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Filters Offcanvas */}
      <Offcanvas
        show={showMobileFilters}
        onHide={() => setShowMobileFilters(false)}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            <i className="bi bi-funnel me-2"></i>Filters
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="d-flex flex-column gap-3">
            <div>
              <Form.Label>Shakisha</Form.Label>
              <div className="search-wrapper" style={{ position: "relative" }}>
                <i className="bi bi-search search-icon"></i>
                <Form.Control
                  placeholder="Amazina, ubumuga..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                  style={{ paddingLeft: "2.25rem" }}
                />
              </div>
            </div>

            <div>
              <Form.Label>Icyiciro</Form.Label>
              <Form.Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">Byose</option>
                <option value="insurance">Bafite Mutuelle</option>
                <option value="no-insurance">Nta Mutuelle</option>
                <option value="disability">Bafite Ubumuga</option>
                <option value="chronic">Indwara Zirambye</option>
              </Form.Select>
            </div>

            <div className="d-grid gap-2 mt-3">
              <Button
                className="btn-primary-custom"
                onClick={() => setShowMobileFilters(false)}
              >
                <i className="bi bi-check me-2"></i>Emeza
              </Button>
              <Button className="btn-outline-custom" onClick={resetFilters}>
                <i className="bi bi-arrow-repeat me-2"></i>Reset
              </Button>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Data Container */}
      <div className="data-container">
        <div className="data-header">
          <h5 className="data-title">
            <i className="bi bi-table"></i>
            <span className="d-none d-sm-inline">Urutonde rw'Ubuzima</span>
            <span className="d-sm-none">Ubuzima</span>
          </h5>
          <div className="data-meta">
            <span className="results-count">{filteredData.length} ibyabonetse</span>
            <div className="view-toggle">
              <button
                className={`view-toggle-btn ${viewMode === "auto" ? "active" : ""}`}
                onClick={() => setViewMode("auto")}
              >
                <i className="bi bi-grid-3x2-gap"></i>
              </button>
              <button
                className={`view-toggle-btn ${viewMode === "cards" ? "active" : ""}`}
                onClick={() => setViewMode("cards")}
              >
                <i className="bi bi-card-heading"></i>
              </button>
              <button
                className={`view-toggle-btn ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
              >
                <i className="bi bi-list-ul"></i>
              </button>
              <button
                className={`view-toggle-btn ${viewMode === "table" ? "active" : ""}`}
                onClick={() => setViewMode("table")}
              >
                <i className="bi bi-table"></i>
              </button>
            </div>
          </div>
        </div>

        {renderDataView()}
        {renderPagination()}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        show={show}
        onHide={() => setShow(false)}
        size={deviceInfo.isMobile ? undefined : "lg"}
        centered
        fullscreen={deviceInfo.width < 576 ? true : undefined}
      >
        <Modal.Header
          closeButton
          style={{
            background: `linear-gradient(135deg, ${theme.primary} 0%, #4f46e5 100%)`,
            color: "white",
            border: "none",
          }}
        >
          <Modal.Title>
            <i className={`bi ${editing ? "bi-pencil-square" : "bi-plus-circle"} me-2`}></i>
            {editing ? "Hindura Amakuru" : "Ongeraho Amakuru"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            {/* Citizen Field */}
            <Form.Group className="mb-3">
              <Form.Label>
                Umuturage <span style={{ color: theme.danger }}>*</span>
              </Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{ background: theme.card, borderColor: theme.border, color: theme.muted }}
                >
                  <i className="bi bi-person"></i>
                </InputGroup.Text>
                <Form.Select
                  name="citizen_id"
                  value={form.citizen_id || ""}
                  onChange={handleCitizenSelect}
                  isInvalid={!!formErrors.amazina_y_umuturage}
                >
                  <option value="">-- Hitamo umuturage --</option>
                  {citizens.map((c) => (
                    <option key={c.umuturage_id} value={c.umuturage_id}>
                      {c.full_name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {formErrors.amazina_y_umuturage}
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>

            <hr className="section-divider" />
            <h6 className="section-title">
              <i className="bi bi-person-wheelchair" style={{ color: theme.danger }}></i>
              Amakuru ku Bumuga
            </h6>

            <Form.Check
              type="switch"
              id="ubumuga"
              name="afite_ubumuga"
              label="Afite Ubumuga"
              checked={form.afite_ubumuga === 1}
              onChange={handleChange}
              className="mb-3"
            />

            {form.afite_ubumuga === 1 && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Ubwoko bw'Ubumuga <span style={{ color: theme.danger }}>*</span>
                </Form.Label>
                <Form.Control
                  name="ubwoko_bw_ubumuga"
                  value={form.ubwoko_bw_ubumuga}
                  onChange={handleChange}
                  placeholder="Nk'ubumuga bwo kubona, kumva..."
                  isInvalid={!!formErrors.ubwoko_bw_ubumuga}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.ubwoko_bw_ubumuga}
                </Form.Control.Feedback>
              </Form.Group>
            )}

            <hr className="section-divider" />
            <h6 className="section-title">
              <i className="bi bi-thermometer-half" style={{ color: theme.warning }}></i>
              Indwara Irambye
            </h6>

            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={2}
                name="indwara_y_igihe_kirekire"
                value={form.indwara_y_igihe_kirekire}
                onChange={handleChange}
                placeholder="Nk'diyabete, tension, etc."
              />
            </Form.Group>

            <hr className="section-divider" />
            <h6 className="section-title">
              <i className="bi bi-shield-check" style={{ color: theme.success }}></i>
              Ubwishingizi bw'Ubuzima
            </h6>

            <Form.Check
              type="switch"
              id="mutuelle"
              name="afite_mutuelle"
              label="Afite Ubwishingizi"
              checked={form.afite_mutuelle === 1}
              onChange={handleChange}
              className="mb-3"
            />

            {form.afite_mutuelle === 1 && (
              <>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Ubwoko bw'Ubwishingizi <span style={{ color: theme.danger }}>*</span>
                      </Form.Label>
                      <Form.Select
                        name="ubwoko_bwa_mutuelle"
                        value={form.ubwoko_bwa_mutuelle}
                        onChange={handleChange}
                        isInvalid={!!formErrors.ubwoko_bwa_mutuelle}
                      >
                        <option value="">Hitamo ubwoko...</option>
                        {insuranceTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {formErrors.ubwoko_bwa_mutuelle}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Ivuriro Ajyamo</Form.Label>
                      <Form.Control
                        name="ivuriro_ajyamo"
                        value={form.ivuriro_ajyamo}
                        onChange={handleChange}
                        placeholder="Nk'CHUK, Polyclinique..."
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow(false)}>
            <i className="bi bi-x-circle me-1"></i>Hagarika
          </Button>
          <Button
            style={{
              background: editing
                ? `linear-gradient(135deg, ${theme.warning}, #e68a00)`
                : `linear-gradient(135deg, ${theme.success}, #00b887)`,
              border: "none",
            }}
            onClick={handleSubmit}
          >
            <i className={`bi me-1 ${editing ? "bi-save" : "bi-check-circle"}`}></i>
            {editing ? "Hindura" : "Bika"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered size="sm">
        <div className="delete-modal-content">
          <div className="delete-icon-wrapper">
            <i className="bi bi-exclamation-triangle-fill"></i>
          </div>
          <h5 className="delete-title">Gusiba Amakuru</h5>
          <p className="delete-message">Uremeza ko ushaka gusiba amakuru y'ubuzima bwa:</p>
          {deleteTarget && (
            <div className="delete-target-box">
              <span className="delete-target-name">
                <i className="bi bi-person-fill me-2" style={{ color: theme.danger }}></i>
                {deleteTarget.amazina_y_umuturage}
              </span>
            </div>
          )}
          <p className="delete-warning">
            <i className="bi bi-info-circle me-1"></i>
            Iki gikorwa ntigishobora gusubizwaho.
          </p>
          <div className="delete-actions">
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              <i className="bi bi-x-circle me-1"></i>Hagarika
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
              style={{
                background: `linear-gradient(135deg, ${theme.danger}, #cc3160)`,
                border: "none",
              }}
            >
              <i className="bi bi-trash me-1"></i>Siba
            </Button>
          </div>
        </div>
      </Modal>

      {/* Details Modal */}
      <Modal
        show={showDetails}
        onHide={() => setShowDetails(false)}
        centered
        size={deviceInfo.isMobile ? undefined : "lg"}
        fullscreen={deviceInfo.width < 576 ? true : undefined}
      >
        <Modal.Header
          closeButton
          style={{
            background: `linear-gradient(135deg, ${theme.info} 0%, #0077cc 100%)`,
            color: "white",
            border: "none",
          }}
        >
          <Modal.Title>
            <i className="bi bi-eye me-2"></i>Amakuru Arambuye
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem && (
            <div>
              {/* Person Info */}
              <div
                style={{
                  background: theme.card,
                  borderRadius: "16px",
                  padding: "1.5rem",
                  textAlign: "center",
                  border: `1px solid ${theme.border}`,
                  marginBottom: "1rem",
                }}
              >
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "20px",
                    background: `linear-gradient(135deg, ${theme.primary}33, ${theme.primary}11)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 1rem",
                  }}
                >
                  <i className="bi bi-person-fill" style={{ fontSize: "2.5rem", color: theme.primary }}></i>
                </div>
                <h4 style={{ color: theme.textBright, fontWeight: 700 }}>
                  {selectedItem.amazina_y_umuturage}
                </h4>
              </div>

              {/* Health Info Grid */}
              <Row className="g-3">
                <Col xs={6}>
                  <div
                    style={{
                      background: theme.card,
                      borderRadius: "14px",
                      padding: "1rem",
                      border: `1px solid ${theme.border}`,
                      borderLeftWidth: "4px",
                      borderLeftColor: selectedItem.afite_ubumuga ? theme.danger : theme.success,
                    }}
                  >
                    <i
                      className="bi bi-person-wheelchair"
                      style={{
                        fontSize: "1.5rem",
                        color: selectedItem.afite_ubumuga ? theme.danger : theme.success,
                      }}
                    ></i>
                    <h6 style={{ color: theme.text, marginTop: "0.5rem", fontSize: "0.8rem" }}>
                      Ubumuga
                    </h6>
                    <StatusBadge value={selectedItem.afite_ubumuga} type="disability" />
                    {selectedItem.ubwoko_bw_ubumuga && (
                      <p style={{ color: theme.muted, fontSize: "0.75rem", marginTop: "0.5rem" }}>
                        {selectedItem.ubwoko_bw_ubumuga}
                      </p>
                    )}
                  </div>
                </Col>
                <Col xs={6}>
                  <div
                    style={{
                      background: theme.card,
                      borderRadius: "14px",
                      padding: "1rem",
                      border: `1px solid ${theme.border}`,
                      borderLeftWidth: "4px",
                      borderLeftColor: selectedItem.afite_mutuelle ? theme.success : theme.danger,
                    }}
                  >
                    <i
                      className={`bi bi-shield-${selectedItem.afite_mutuelle ? "check" : "x"}`}
                      style={{
                        fontSize: "1.5rem",
                        color: selectedItem.afite_mutuelle ? theme.success : theme.danger,
                      }}
                    ></i>
                    <h6 style={{ color: theme.text, marginTop: "0.5rem", fontSize: "0.8rem" }}>
                      Mutuelle
                    </h6>
                    <StatusBadge value={selectedItem.afite_mutuelle} type="insurance" />
                    {selectedItem.ubwoko_bwa_mutuelle && (
                      <p style={{ color: theme.muted, fontSize: "0.75rem", marginTop: "0.5rem" }}>
                        {selectedItem.ubwoko_bwa_mutuelle}
                      </p>
                    )}
                  </div>
                </Col>
              </Row>

              {(selectedItem.indwara_y_igihe_kirekire || selectedItem.ivuriro_ajyamo) && (
                <Row className="g-3 mt-1">
                  {selectedItem.indwara_y_igihe_kirekire && (
                    <Col xs={12} md={6}>
                      <div
                        style={{
                          background: theme.card,
                          borderRadius: "14px",
                          padding: "1rem",
                          border: `1px solid ${theme.border}`,
                          borderLeftWidth: "4px",
                          borderLeftColor: theme.warning,
                        }}
                      >
                        <i className="bi bi-thermometer-half" style={{ fontSize: "1.5rem", color: theme.warning }}></i>
                        <h6 style={{ color: theme.text, marginTop: "0.5rem", fontSize: "0.8rem" }}>
                          Indwara Irambye
                        </h6>
                        <p style={{ color: theme.textBright, marginTop: "0.5rem", marginBottom: 0 }}>
                          {selectedItem.indwara_y_igihe_kirekire}
                        </p>
                      </div>
                    </Col>
                  )}
                  {selectedItem.ivuriro_ajyamo && (
                    <Col xs={12} md={6}>
                      <div
                        style={{
                          background: theme.card,
                          borderRadius: "14px",
                          padding: "1rem",
                          border: `1px solid ${theme.border}`,
                          borderLeftWidth: "4px",
                          borderLeftColor: theme.info,
                        }}
                      >
                        <i className="bi bi-hospital" style={{ fontSize: "1.5rem", color: theme.info }}></i>
                        <h6 style={{ color: theme.text, marginTop: "0.5rem", fontSize: "0.8rem" }}>
                          Ivuriro
                        </h6>
                        <p style={{ color: theme.textBright, marginTop: "0.5rem", marginBottom: 0 }}>
                          {selectedItem.ivuriro_ajyamo}
                        </p>
                      </div>
                    </Col>
                  )}
                </Row>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            style={{ background: theme.primary, border: "none" }}
            onClick={() => {
              setShowDetails(false);
              handleEdit(selectedItem);
            }}
          >
            <i className="bi bi-pencil-square me-1"></i>Hindura
          </Button>
          <Button variant="secondary" onClick={() => setShowDetails(false)}>
            <i className="bi bi-x-circle me-1"></i>Funga
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Ubuzima;